import SiteMessagesInstance from '../../components/site-messages'
import { isUsingPipeStrap, pipeStrapLabel } from '../../util/selective-pipe-strap'

const VIEW_QUESTION_SYSTEM_TYPE = 'question_system_type'
const VIEW_HELP_SYSTEM_TYPE = 'help_system_type'

const SYSTEM_WET = 'wet'
const SYSTEM_DRY = 'dry'
const SYSTEM_WET_AND_DRY = 'wet_and_dry'

const NEXT_STEP_ID = 'after_setup_name_sensor'
const PREVIOUS_STEP_ID = 'step_setup_contacts'

// The system-type half of the selective questionnaire. The pipe-placement
// question is asked much earlier — on the connected pipe-question step, at the
// install spot — but the phone has no internet there, so its answer rides on
// the onboarding instance and is submitted here together with this one.
export class StepAfterSetupSelectiveQuestions {
  constructor (onboarding) {
    this.onboarding = onboarding

    this.currentView = VIEW_QUESTION_SYSTEM_TYPE
    this.isSubmitting = false

    this.answers = {
      sprinkler_system_type: ''
    }
  }

  id = 'after_setup_selective_questions'
  title = 'Sensor Setup'

  template = `
  <div class="onboarding-step" data-for="after_setup_selective_questions">
    <div class="onboarding-selective-questions-stage"></div>
  </div>
  `

  onActivate = () => {
    this.onboarding.showBackButton()
    this.render()
  }

  onBack = () => {
    if (this.currentView === VIEW_HELP_SYSTEM_TYPE) {
      this.currentView = VIEW_QUESTION_SYSTEM_TYPE
      this.render()
      return
    }

    // On the question, back leaves for the contacts step behind it
    this.onboarding.goToStep(PREVIOUS_STEP_ID)

  }

  bindActions = () => {
    this.$root = $(this.onboarding.container).find('[data-for="after_setup_selective_questions"]')
    this.$stage = this.$root.find('.onboarding-selective-questions-stage')

    this.$root.on('change', 'input[name="sprinkler_system_type"]', e => {
      this.answers.sprinkler_system_type = String(e.currentTarget.value || '')
      this.render()
    })

    this.$root.on('click', '[data-for="system_type_continue"]', async () => {
      if (this.isSubmitting || !this.answers.sprinkler_system_type) return
      await this.submitAnswers()
    })

    this.$root.on('click', '[data-for="system_type_open_help"]', () => {
      this.currentView = VIEW_HELP_SYSTEM_TYPE
      this.render()
    })

    this.$root.on('click', '[data-for="system_type_close_help"]', () => {
      this.currentView = VIEW_QUESTION_SYSTEM_TYPE
      this.render()
    })

    // Inline back button
    this.$root.on('click', '[data-for="selective_back"]', () => {
      this.onBack()
    })
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Getters / answer formatters
  // ────────────────────────────────────────────────────────────────────────────

  getSystemTypeAnswerValue() {
    if (this.answers.sprinkler_system_type === SYSTEM_WET) return 'Wet System'
    if (this.answers.sprinkler_system_type === SYSTEM_DRY) return 'Dry System'
    if (this.answers.sprinkler_system_type === SYSTEM_WET_AND_DRY) return 'Wet & Dry System'
    return ''
  }

  getSystemTypeOptions() {
    return [
      {
        value: SYSTEM_WET,
        heading: 'WET SYSTEM',
        description: 'Usually found in heated interior spaces.'
      },
      {
        value: SYSTEM_DRY,
        heading: 'DRY SYSTEM',
        description: 'Often used in colder or unheated areas.'
      },
      {
        value: SYSTEM_WET_AND_DRY,
        heading: 'WET & DRY SYSTEM',
        description: 'Mixed heated and unheated areas.'
      }
    ]
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Submission
  // ────────────────────────────────────────────────────────────────────────────

  getSensorId() {
    if (this.onboarding && this.onboarding.sensorId) {
      return this.onboarding.sensorId
    }

    if (this.onboarding && this.onboarding.sensor && this.onboarding.sensor.id) {
      return this.onboarding.sensor.id
    }

    if (this.onboarding && this.onboarding.sensor && this.onboarding.sensor.sid) {
      return this.onboarding.sensor.sid
    }

    return null
  }

  getAnswerPayload() {
    const pipe = this.onboarding.pipe_strap_location

    return {
      sprinkler_system_type: this.getSystemTypeAnswerValue(),
      pipe_strap_used: isUsingPipeStrap(pipe),
      pipe_strap_location: pipeStrapLabel(pipe)
    }
  }

  async submitAnswers() {
    if (this.isSubmitting) {
      return
    }

    const sensorId = this.getSensorId()

    if (!sensorId) {
      SiteMessagesInstance.addMessage('error', 'Unable to determine which sensor to save these answers for.')
      SiteMessagesInstance.displayMessages()
      return
    }

    this.isSubmitting = true
    this.render()

    try {
      const r = await window.apiRequest(
        'post',
        `/sensor/${encodeURIComponent(sensorId)}/questionaire`,
        this.getAnswerPayload()
      )

      if (!r || r.type === 'error' || r.t === 'err' || r.err == 1) {
        throw new Error('Unable to save questionnaire')
      }

      this.isSubmitting = false
      this.onboarding.goToStep(NEXT_STEP_ID)
    } catch (e) {
      this.isSubmitting = false

      SiteMessagesInstance.addMessage(
        'error',
        'Unable to save the sensor setup. Please try again.'
      )
      SiteMessagesInstance.displayMessages()

      this.render()
    }
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Utilities
  // ────────────────────────────────────────────────────────────────────────────

  escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, char => {
      switch (char) {
        case '&': return '&amp;'
        case '<': return '&lt;'
        case '>': return '&gt;'
        case '"': return '&quot;'
        case '\'': return '&#39;'
        default: return char
      }
    })
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Rendering
  // ────────────────────────────────────────────────────────────────────────────

  renderSystemTypeView() {
    const options = this.getSystemTypeOptions()
    const continueDisabled = !this.answers.sprinkler_system_type || this.isSubmitting
    const continueLabel = this.isSubmitting ? 'Saving...' : 'Continue'

    const optionsHtml = options.map(option => {
      const checked = this.answers.sprinkler_system_type === option.value

      return `
        <label class="onboarding-selective-questions__option onboarding-selective-questions__option--rich ${checked ? 'is-selected' : ''}">
          <input
            type="radio"
            name="sprinkler_system_type"
            value="${this.escapeHtml(option.value)}"
            ${checked ? 'checked' : ''}
          />
          <div class="onboarding-selective-questions__option-body">
            <div class="onboarding-selective-questions__option-heading">
              ${this.escapeHtml(option.heading)}
            </div>
            <div class="onboarding-selective-questions__option-description">
              ${this.escapeHtml(option.description)}
            </div>
          </div>
        </label>
      `
    }).join('')

    this.$stage.html(`
      <div class="onboarding-selective-questions">
        <div class="onboarding-selective-questions__card">
          <div class="onboarding-selective-questions__title">
            What type of fire sprinkler system is this?
          </div>

          <div class="onboarding-selective-questions__options">
            ${optionsHtml}
          </div>

          <div class="onboarding-selective-questions__actions">
            <button
              type="button"
              class="button button--grey-outline"
              data-for="selective_back"
            >
              Back
            </button>

            <button
              type="button"
              class="button button--medium onboarding-selective-questions__continue"
              data-for="system_type_continue"
              ${continueDisabled ? 'disabled' : ''}
            >
              ${continueLabel}
            </button>
          </div>

          <button
            type="button"
            class="onboarding-selective-questions__help-link"
            data-for="system_type_open_help"
          >
            Help me determine system type
          </button>
        </div>
      </div>
    `)
  }

  renderHelpView() {
    this.$stage.html(`
      <div class="onboarding-selective-questions">
        <div class="onboarding-selective-questions__card onboarding-selective-questions__card--help">
          <div class="onboarding-selective-questions__title">
            How to determine your sprinkler system type
          </div>

          <div class="onboarding-selective-questions-help">
            <div class="onboarding-selective-questions-help__item">
              <div class="onboarding-selective-questions-help__heading">Wet system</div>
              <div class="onboarding-selective-questions-help__text">Pipes are always filled with water.</div>
              <div class="onboarding-selective-questions-help__text">Common in heated interior spaces like offices, hotels, and apartments.</div>
              <div class="onboarding-selective-questions-help__text">The riser room typically has a single alarm check valve and no air compressor.</div>
            </div>

            <div class="onboarding-selective-questions-help__item">
              <div class="onboarding-selective-questions-help__heading">Dry system</div>
              <div class="onboarding-selective-questions-help__text">Pipes are filled with pressurized air or nitrogen until a sprinkler activates.</div>
              <div class="onboarding-selective-questions-help__text">Common in unheated or cold areas like parking garages, attics, freezer rooms, and outdoor canopies.</div>
              <div class="onboarding-selective-questions-help__text">Look for a small air compressor or nitrogen tank near the dry-pipe valve in the riser room.</div>
            </div>

            <div class="onboarding-selective-questions-help__item">
              <div class="onboarding-selective-questions-help__heading">Both wet &amp; dry</div>
              <div class="onboarding-selective-questions-help__text">Some buildings use both: wet pipes serve heated areas and dry pipes serve unheated areas.</div>
              <div class="onboarding-selective-questions-help__text">If you see both valve types in the riser room — or if part of the building is heated and another part isn't — choose Wet &amp; Dry.</div>
            </div>

            <div class="onboarding-selective-questions-help__item">
              <div class="onboarding-selective-questions-help__heading">Still not sure?</div>
              <div class="onboarding-selective-questions-help__text">Ask your fire safety inspector or building manager — they'll know which system this is.</div>
            </div>
          </div>

          <div class="onboarding-selective-questions__actions">
            <button
              type="button"
              class="button button--grey-outline"
              data-for="system_type_close_help"
            >
              Back
            </button>
          </div>
        </div>
      </div>
    `)
  }

  render() {
    if (!this.$stage || this.$stage.length === 0) {
      return
    }

    if (this.currentView === VIEW_HELP_SYSTEM_TYPE) {
      this.renderHelpView()
      return
    }

    this.renderSystemTypeView()
  }
}
